import { useState } from "react";
import {
  Box, Grid, Typography, Stack, TextField, Button,
  FormControl, InputLabel, Select, MenuItem,
  IconButton, alpha, CircularProgress,
} from "@mui/material";
import {
  Email as EmailIcon, Phone as PhoneIcon,
  LocationOn as LocationIcon, Language as GlobeIcon,
  LinkedIn as LinkedInIcon, CheckCircle as CheckIcon,
  Send as SendIcon, ErrorOutline as ErrorIcon,
} from "@mui/icons-material";

// ── Palette ──────────────────────────────────────────────────────
const WHITE      = "#ffffff";
const OFF_WHITE  = "#f7f4f1";
const BROWN      = "#5C3311";
const BROWN_DARK = "#3e220b";
const GOLD       = "#DDA63A";
const TEXT_DARK  = "#1a0e05";
const TEXT_MID   = "#4a3020";
const TEXT_LIGHT = "#8a6a50";

const SDG_BAR = [
  "#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21","#26BDE2",
  "#FCC30B","#A21942","#FD6925","#DD1367","#FD9D24","#BF8B2E",
  "#3F7E44","#0A97D9","#56C02B","#00689D","#19486A",
];

const contactDetails = [
  { icon: <EmailIcon />,    label: "Email",         value: "africabuildtoshelter@gmail.com" },
  { icon: <PhoneIcon />,    label: "Phone (Lewis)", value: "+256 761 070 710" },
  { icon: <LocationIcon />, label: "Head Office",   value: "Kampala, Central Region, Uganda" },
  { icon: <GlobeIcon />,    label: "Reach",         value: "Pan-African · All 54 Countries" },
];

const reasonOptions = [
  { label: "Volunteer Inquiry",         value: "volunteer"   },
  { label: "Partnership / Sponsorship", value: "partnership" },
  { label: "Donation",                  value: "donate"      },
  { label: "Media / Press",             value: "media"       },
  { label: "General Question",          value: "general"     },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "'DM Sans', sans-serif",
    borderRadius: "8px",
    "& fieldset": { borderColor: alpha(BROWN, 0.22) },
    "&:hover fieldset": { borderColor: alpha(BROWN, 0.45) },
    "&.Mui-focused fieldset": { borderColor: BROWN, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'DM Sans', sans-serif",
    color: TEXT_LIGHT,
    "&.Mui-focused": { color: BROWN },
  },
  "& .MuiInputBase-input": {
    color: TEXT_DARK,
    fontFamily: "'DM Sans', sans-serif",
  },
  "& .MuiSelect-select": {
    fontFamily: "'DM Sans', sans-serif",
    color: TEXT_DARK,
  },
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://www.africabuildtoshelter.com/api";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  const [form, setForm] = useState({
    first_name: "",
    last_name:  "",
    email:      "",
    country:    "",
    reason:     "",
    message:    "",
  });

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (apiError) setApiError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setApiError("");

    try {
      const res = await fetch(`${API_BASE}/contact/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name:  form.last_name.trim(),
          email:      form.email.trim(),
          country:    form.country.trim(),
          reason:     form.reason || "general",
          message:    form.message.trim(),
        }),
      });

      if (res.status === 201) {
        setSubmitted(true);
      } else {
        const data = await res.json();
        const firstKey = Object.keys(data)[0];
        const firstMsg = Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey];
        setApiError(firstMsg || "Something went wrong. Please try again.");
      }
    } catch {
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      id="contact"
      sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}
    >
      {/* ══ Info Panel — full bleed image, no brown background ══════ */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: { xs: 540, md: "100%" },
          backgroundImage: "url('/contact.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "right center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Minimal dark scrim — just enough for text legibility, image stays visible */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(160deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.65) 100%)",
          }}
        />

        {/* SDG top bar */}
        <Box
          sx={{
            position: "absolute", top: 0, left: 0, right: 0,
            height: "5px", display: "flex", zIndex: 2,
          }}
        >
          {SDG_BAR.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
        </Box>

        {/* All content sits above the scrim */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            py: { xs: 8, md: 12 },
            px: { xs: 3, md: 7 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 32, height: 3,
                background: `linear-gradient(90deg, #E5243B, ${GOLD})`,
                borderRadius: 2,
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: "11px",
                color: alpha(WHITE, 0.75),
                letterSpacing: "2.5px",
                textTransform: "uppercase",
              }}
            >
              Reach Us
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: { xs: "2rem", md: "2.6rem" },
              fontWeight: 800,
              lineHeight: 1.15,
              color: WHITE,
              mb: 1.5,
              textShadow: "0 2px 12px rgba(0,0,0,0.4)",
            }}
          >
            Let's Build<br />
            <Box component="span" sx={{ color: GOLD }}>Africa Together</Box>
          </Typography>

          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              color: alpha(WHITE, 0.85),
              fontSize: "15px",
              lineHeight: 1.8,
              mb: 5,
              maxWidth: 380,
              textShadow: "0 1px 6px rgba(0,0,0,0.3)",
            }}
          >
            Whether you're looking to partner, volunteer, donate, or simply learn
            more — we'd love to hear from you.
          </Typography>

          {contactDetails.map((item) => (
            <Stack
              key={item.label}
              direction="row"
              spacing={2}
              alignItems="flex-start"
              sx={{ mb: 3 }}
            >
              <Box
                sx={{
                  bgcolor: alpha(WHITE, 0.15),
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  border: `1px solid ${alpha(WHITE, 0.35)}`,
                  borderRadius: 2,
                  width: 44, height: 44,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: GOLD,
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    color: GOLD,
                    mb: 0.4,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: WHITE,
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: 1.5,
                    textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            </Stack>
          ))}

          <Box sx={{ height: "1px", bgcolor: alpha(WHITE, 0.2), my: 3.5 }} />

          <Stack direction="row" spacing={1.5}>
            <IconButton
              href="https://www.linkedin.com/company/build-to-shelter-africa/"
              target="_blank"
              sx={{
                bgcolor: alpha(WHITE, 0.15),
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: `1px solid ${alpha(WHITE, 0.35)}`,
                color: WHITE,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: alpha(WHITE, 0.28),
                  color: GOLD,
                  borderColor: alpha(GOLD, 0.6),
                },
                transition: "all 0.25s",
              }}
            >
              <LinkedInIcon />
            </IconButton>
            <IconButton
              href="mailto:africabuildtoshelter@gmail.com"
              sx={{
                bgcolor: alpha(WHITE, 0.15),
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: `1px solid ${alpha(WHITE, 0.35)}`,
                color: WHITE,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: alpha(WHITE, 0.28),
                  color: GOLD,
                  borderColor: alpha(GOLD, 0.6),
                },
                transition: "all 0.25s",
              }}
            >
              <EmailIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* ══ Form Panel ══════════════════════════════════════════════ */}
      <Box
        sx={{
          bgcolor: OFF_WHITE,
          py: { xs: 8, md: 12 },
          px: { xs: 3, md: 7 },
          borderTop: { xs: `4px solid ${BROWN}`, md: "none" },
          borderLeft: { md: `4px solid ${BROWN}` },
          position: "relative",
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 32, height: 3,
                background: `linear-gradient(90deg, #E5243B, ${GOLD})`,
                borderRadius: 2,
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: "11px",
                color: TEXT_LIGHT,
                letterSpacing: "2.5px",
                textTransform: "uppercase",
              }}
            >
              Get In Touch
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: { xs: "1.7rem", md: "2rem" },
              fontWeight: 800,
              color: TEXT_DARK,
              lineHeight: 1.2,
              mb: 0.8,
            }}
          >
            Send Us a Message
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: TEXT_LIGHT, fontSize: "14px" }}>
            We'll get back to you within 48 hours.
          </Typography>
        </Box>

        <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField
                label="First Name" fullWidth required sx={fieldSx}
                value={form.first_name}
                onChange={handleChange("first_name")}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Last Name" fullWidth required sx={fieldSx}
                value={form.last_name}
                onChange={handleChange("last_name")}
              />
            </Grid>
          </Grid>

          <TextField
            label="Email Address" type="email" fullWidth required sx={fieldSx}
            value={form.email}
            onChange={handleChange("email")}
          />

          <TextField
            label="Country" fullWidth sx={fieldSx}
            value={form.country}
            onChange={handleChange("country")}
          />

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel>Reason for Contact</InputLabel>
            <Select
              label="Reason for Contact"
              value={form.reason}
              onChange={handleChange("reason")}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: WHITE,
                    border: `1px solid ${alpha(BROWN, 0.18)}`,
                    boxShadow: `0 8px 32px ${alpha(BROWN, 0.15)}`,
                    "& .MuiMenuItem-root": {
                      fontFamily: "'DM Sans', sans-serif",
                      color: TEXT_DARK,
                      fontSize: "14px",
                      "&:hover": { bgcolor: alpha(BROWN, 0.06) },
                      "&.Mui-selected": { bgcolor: alpha(BROWN, 0.1), color: BROWN, fontWeight: 700 },
                    },
                  },
                },
              }}
            >
              {reasonOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Message" multiline rows={4} fullWidth required sx={fieldSx}
            placeholder="Tell us about yourself or your inquiry..."
            value={form.message}
            onChange={handleChange("message")}
          />

          {apiError && (
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                bgcolor: alpha("#c0392b", 0.07),
                border: `1px solid ${alpha("#c0392b", 0.25)}`,
                borderLeft: "4px solid #c0392b",
                borderRadius: 2, p: 2,
              }}
            >
              <ErrorIcon sx={{ color: "#c0392b", flexShrink: 0 }} />
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: "#c0392b", fontSize: "14px" }}>
                {apiError}
              </Typography>
            </Box>
          )}

          {submitted ? (
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                bgcolor: alpha(BROWN, 0.07),
                border: `1px solid ${alpha(BROWN, 0.25)}`,
                borderLeft: `4px solid ${BROWN}`,
                borderRadius: 2, p: 2.5,
              }}
            >
              <CheckIcon sx={{ color: BROWN, flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: BROWN, fontWeight: 700, fontSize: "14px" }}>
                  Message sent successfully!
                </Typography>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: TEXT_MID, fontSize: "13px", mt: 0.3 }}>
                  We'll be in touch within 48 hours.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              endIcon={
                submitting
                  ? <CircularProgress size={18} sx={{ color: WHITE }} />
                  : <SendIcon />
              }
              sx={{
                bgcolor: BROWN,
                color: "#fff",
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: "13px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                py: 1.8,
                borderRadius: "8px",
                boxShadow: `0 4px 20px ${alpha(BROWN, 0.35)}`,
                "&:hover": {
                  bgcolor: BROWN_DARK,
                  boxShadow: `0 6px 28px ${alpha(BROWN, 0.45)}`,
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": { bgcolor: alpha(BROWN, 0.5), color: WHITE },
                transition: "all 0.25s ease",
              }}
            >
              {submitting ? "Sending…" : "Send Message"}
            </Button>
          )}
        </Stack>

        <Typography
          sx={{
            fontFamily: "'Space Mono', monospace",
            fontSize: "10px",
            color: alpha(TEXT_LIGHT, 0.6),
            mt: 3,
            letterSpacing: "1px",
            textAlign: "center",
          }}
        >
          BUILD TO SHELTER AFRICA · KAMPALA, UGANDA
        </Typography>
      </Box>
    </Box>
  );
}